/**
 * Expression Evaluator
 * Safe, secure expression evaluation for form field bindings
 * Supports: variables, nested paths, arithmetic, functions, conditionals
 */

/**
 * Type for functions registered in the evaluation context.
 * Accepts any arguments and returns an unknown value, enabling type-safe
 * registration without constraining callers to a specific arity.
 */
export type EvaluatorFunction = (...args: unknown[]) => unknown;

/**
 * Evaluation context containing data and functions for expression evaluation
 */
export interface EvaluationContext {
  /** Current form field values */
  values: Record<string, any>;

  /** External data sources */
  data?: Record<string, any>;

  /** Whitelisted safe functions */
  functions?: Record<string, EvaluatorFunction>;

  /** Application configuration */
  config?: Record<string, any>;

  /** Custom variables */
  variables?: Record<string, any>;
}

/**
 * Parsed expression result
 */
interface ParsedExpression {
  type: 'literal' | 'variable' | 'function' | 'binary' | 'conditional' | 'array-access';
  value?: any;
  expression?: string;
  left?: ParsedExpression;
  right?: ParsedExpression;
  operator?: string;
  test?: ParsedExpression;
  consequent?: ParsedExpression;
  alternate?: ParsedExpression;
  object?: ParsedExpression;
  index?: ParsedExpression;
  args?: ParsedExpression[];
  callee?: string;
}

/**
 * Expression Evaluator - Safe expression parsing and evaluation
 * Prevents code injection and provides secure function execution
 */
export class ExpressionEvaluator {
  private static readonly WHITELISTED_FUNCTIONS = {
    // Math functions
    Math: {
      abs: Math.abs,
      ceil: Math.ceil,
      floor: Math.floor,
      round: Math.round,
      max: Math.max,
      min: Math.min,
      pow: Math.pow,
      sqrt: Math.sqrt,
      trunc: Math.trunc,
    },
    // String functions
    String: {
      toUpperCase: (str: any) => String(str).toUpperCase(),
      toLowerCase: (str: any) => String(str).toLowerCase(),
      trim: (str: any) => String(str).trim(),
      substring: (str: any, start: number, end?: number) => String(str).substring(start, end),
      charAt: (str: any, idx: number) => String(str).charAt(idx),
      indexOf: (str: any, search: string) => String(str).indexOf(search),
      includes: (str: any, search: string) => String(str).includes(search),
      startsWith: (str: any, search: string) => String(str).startsWith(search),
      endsWith: (str: any, search: string) => String(str).endsWith(search),
      split: (str: any, sep: string) => String(str).split(sep),
    },
    // Array functions
    Array: {
      isArray: Array.isArray,
      length: (arr: any[]) => Array.isArray(arr) ? arr.length : 0,
      join: (arr: any[], sep?: string) => Array.isArray(arr) ? arr.join(sep) : '',
      filter: (arr: any[], fn: Function) => Array.isArray(arr) ? arr.filter((v, i) => fn(v, i)) : [],
      map: (arr: any[], fn: Function) => Array.isArray(arr) ? arr.map((v, i) => fn(v, i)) : [],
    },
    // JSON
    JSON: {
      stringify: JSON.stringify,
      parse: JSON.parse,
    },
    // Number functions
    Number: {
      isNaN: Number.isNaN,
      isFinite: Number.isFinite,
      parseInt: parseInt,
      parseFloat: parseFloat,
    },
  };

  /**
   * Evaluate a simple variable binding like "fieldName" or "user.profile.email"
   */
  static evaluateVariable(path: string, context: EvaluationContext): any {
    try {
      return this.resolvePathInContext(path, context);
    } catch (error) {
      console.warn(`Failed to evaluate variable '${path}':`, error);
      return null;
    }
  }

  /**
   * Evaluate a complete expression like "{{user.name}}" or "{{quantity * price}}"
   */
  static evaluate(expression: string, context: EvaluationContext): any {
    try {
      // Remove wrapper if present
      const cleaned = this.cleanExpression(expression);

      // Try simple variable first (common case)
      if (!this.containsOperators(cleaned)) {
        return this.resolvePathInContext(cleaned, context);
      }

      // Parse and evaluate complex expression
      const parsed = this.parseExpression(cleaned);
      return this.evaluateParsed(parsed, context);
    } catch (error) {
      console.warn(`Failed to evaluate expression '${expression}':`, error);
      return null;
    }
  }

  /**
   * Evaluate a mathematical formula
   */
  static evaluateFormula(formula: string, context: EvaluationContext): number | null {
    try {
      const cleaned = this.cleanExpression(formula);
      const result = this.evaluate(cleaned, context);
      const num = Number(result);
      return !Number.isNaN(num) ? num : null;
    } catch (error) {
      console.warn(`Failed to evaluate formula '${formula}':`, error);
      return null;
    }
  }

  /**
   * Evaluate a conditional expression
   */
  static evaluateCondition(condition: string, context: EvaluationContext): boolean {
    try {
      const cleaned = this.cleanExpression(condition);
      const result = this.evaluate(cleaned, context);
      return Boolean(result);
    } catch (error) {
      console.warn(`Failed to evaluate condition '${condition}':`, error);
      return false;
    }
  }

  /**
   * Evaluate a ternary expression like "age >= 18 ? 'Adult' : 'Minor'"
   */
  static evaluateTernary(
    condition: string,
    consequent: string,
    alternate: string,
    context: EvaluationContext
  ): any {
    try {
      const test = this.evaluateCondition(condition, context);
      return test ? this.evaluate(consequent, context) : this.evaluate(alternate, context);
    } catch (error) {
      console.warn(`Failed to evaluate ternary expression:`, error);
      return null;
    }
  }

  /**
   * Batch evaluate multiple expressions
   */
  static evaluateBatch(
    expressions: Record<string, string>,
    context: EvaluationContext
  ): Record<string, any> {
    const results: Record<string, any> = {};

    for (const [key, expr] of Object.entries(expressions)) {
      results[key] = this.evaluate(expr, context);
    }

    return results;
  }

  /**
   * Check if an expression contains any operators
   */
  private static containsOperators(expr: string): boolean {
    // List of operators to detect
    const operators = ['+', '-', '*', '/', '%', '===', '!==', '==', '!=', '>=', '<=', '>', '<', '&&', '||', '!', '?', ':'];
    return operators.some(op => expr.includes(op));
  }

  /**
   * Clean expression by removing {{ }} wrapper
   */
  private static cleanExpression(expr: string): string {
    let cleaned = expr.trim();
    if (cleaned.startsWith('{{') && cleaned.endsWith('}}')) {
      cleaned = cleaned.slice(2, -2).trim();
    }
    return cleaned;
  }

  /**
   * Resolve a path in the context (e.g., "user.profile.email")
   */
  private static resolvePathInContext(path: string, context: EvaluationContext): any {
    const trimmedPath = path.trim();

    // Handle literals
    if (trimmedPath.startsWith('"') && trimmedPath.endsWith('"')) {
      return trimmedPath.slice(1, -1);
    }
    if (trimmedPath.startsWith("'") && trimmedPath.endsWith("'")) {
      return trimmedPath.slice(1, -1);
    }
    if (trimmedPath === 'true') return true;
    if (trimmedPath === 'false') return false;
    if (trimmedPath === 'null') return null;
    if (trimmedPath === 'undefined') return undefined;

    // Check if it's a number
    const num = Number(trimmedPath);
    if (!Number.isNaN(num)) {
      return num;
    }

    // Try to resolve as path
    return this.resolvePath(trimmedPath, context);
  }

  /**
   * Resolve a dot-notation path like "user.profile.email"
   */
  private static resolvePath(path: string, context: EvaluationContext): any {
    const parts = path.split('.');

    // Try values first
    let current = context.values[parts[0]];
    if (current !== undefined) {
      return this.traversePath(current, parts, 1);
    }

    // Try data
    if (context.data) {
      current = context.data[parts[0]];
      if (current !== undefined) {
        return this.traversePath(current, parts, 1);
      }
    }

    // Try variables
    if (context.variables) {
      current = context.variables[parts[0]];
      if (current !== undefined) {
        return this.traversePath(current, parts, 1);
      }
    }

    return null;
  }

  /**
   * Safely traverse a path through an object, handling null/undefined at each level
   */
  private static traversePath(current: any, parts: string[], startIndex: number): any {
    for (let i = startIndex; i < parts.length; i++) {
      // Type guard: stop if current is null or undefined
      if (current === null || current === undefined) {
        return null;
      }

      // Type guard: stop if current is not an object (primitive types)
      if (typeof current !== 'object') {
        return null;
      }

      // Type guard: ensure the key exists before accessing
      const nextKey = parts[i];
      if (!nextKey || !(nextKey in current)) {
        return null;
      }

      // Safe access to property
      current = (current as Record<string, any>)[nextKey];
    }

    return current;
  }

  /**
   * Parse expression into AST-like structure
   */
  private static parseExpression(expr: string): ParsedExpression {
    // Handle ternary (lowest precedence)
    const ternaryMatch = this.findTernary(expr);
    if (ternaryMatch) {
      return {
        type: 'conditional',
        test: this.parseExpression(ternaryMatch.test),
        consequent: this.parseExpression(ternaryMatch.consequent),
        alternate: this.parseExpression(ternaryMatch.alternate),
      };
    }

    // Handle logical OR (||)
    const orMatch = this.findBinaryOperator(expr, ['||']);
    if (orMatch) {
      return {
        type: 'binary',
        left: this.parseExpression(orMatch.left),
        operator: '||',
        right: this.parseExpression(orMatch.right),
      };
    }

    // Handle logical AND (&&)
    const andMatch = this.findBinaryOperator(expr, ['&&']);
    if (andMatch) {
      return {
        type: 'binary',
        left: this.parseExpression(andMatch.left),
        operator: '&&',
        right: this.parseExpression(andMatch.right),
      };
    }

    // Handle comparison operators
    const compMatch = this.findBinaryOperator(expr, ['===', '!==', '==', '!=', '>=', '<=', '>', '<']);
    if (compMatch) {
      return {
        type: 'binary',
        left: this.parseExpression(compMatch.left),
        operator: compMatch.operator,
        right: this.parseExpression(compMatch.right),
      };
    }

    // Handle addition and subtraction
    const addMatch = this.findBinaryOperator(expr, ['+', '-']);
    if (addMatch) {
      return {
        type: 'binary',
        left: this.parseExpression(addMatch.left),
        operator: addMatch.operator,
        right: this.parseExpression(addMatch.right),
      };
    }

    // Handle multiplication, division, modulo
    const multMatch = this.findBinaryOperator(expr, ['*', '/', '%']);
    if (multMatch) {
      return {
        type: 'binary',
        left: this.parseExpression(multMatch.left),
        operator: multMatch.operator,
        right: this.parseExpression(multMatch.right),
      };
    }

    // Handle function calls
    const funcMatch = this.findFunctionCall(expr);
    if (funcMatch) {
      return {
        type: 'function',
        callee: funcMatch.name,
        args: funcMatch.args.map(arg => this.parseExpression(arg)),
      };
    }

    // Handle array access
    const arrayMatch = this.findArrayAccess(expr);
    if (arrayMatch) {
      return {
        type: 'array-access',
        object: this.parseExpression(arrayMatch.object),
        index: this.parseExpression(arrayMatch.index),
      };
    }

    // Handle literals and variables
    return this.parseLiteral(expr);
  }

  /**
   * Parse a literal or variable
   */
  private static parseLiteral(expr: string): ParsedExpression {
    const trimmed = expr.trim();

    // String literals
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      return {
        type: 'literal',
        value: trimmed.slice(1, -1),
      };
    }

    // Boolean and null literals
    if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null') {
      return {
        type: 'literal',
        value: trimmed === 'true' ? true : trimmed === 'false' ? false : null,
      };
    }

    // Number literals
    const num = Number(trimmed);
    if (!Number.isNaN(num) && !trimmed.includes('.') && !isNaN(Number(trimmed))) {
      return {
        type: 'literal',
        value: num,
      };
    }

    // Variable
    return {
      type: 'variable',
      expression: trimmed,
    };
  }

  /**
   * Find and parse ternary expression
   */
  private static findTernary(expr: string): { test: string; consequent: string; alternate: string } | null {
    let depth = 0;
    let questionIndex = -1;
    let colonIndex = -1;

    for (let i = 0; i < expr.length; i++) {
      const char = expr[i];

      // Track parentheses depth
      if (char === '(' || char === '[' || char === '{') depth++;
      if (char === ')' || char === ']' || char === '}') depth--;

      // Find ? and : at depth 0
      if (depth === 0) {
        if (char === '?' && questionIndex === -1) {
          questionIndex = i;
        } else if (char === ':' && questionIndex !== -1 && colonIndex === -1) {
          colonIndex = i;
        }
      }
    }

    if (questionIndex !== -1 && colonIndex !== -1) {
      return {
        test: expr.substring(0, questionIndex),
        consequent: expr.substring(questionIndex + 1, colonIndex),
        alternate: expr.substring(colonIndex + 1),
      };
    }

    return null;
  }

  /**
   * Find binary operator in expression
   */
  private static findBinaryOperator(
    expr: string,
    operators: string[]
  ): { left: string; operator: string; right: string } | null {
    let depth = 0;

    for (let i = expr.length - 1; i >= 0; i--) {
      const char = expr[i];

      // Track parentheses depth (reverse scan)
      if (char === ')' || char === ']' || char === '}') depth++;
      if (char === '(' || char === '[' || char === '{') depth--;

      if (depth === 0) {
        // Check for operators (longest first to avoid partial matches)
        for (const op of operators.sort((a, b) => b.length - a.length)) {
          if (expr.substr(i - op.length + 1, op.length) === op && expr[i] === op[op.length - 1]) {
            const leftPart = expr.substring(0, i - op.length + 1).trim();
            const rightPart = expr.substring(i + 1).trim();

            if (leftPart && rightPart) {
              return {
                left: leftPart,
                operator: op,
                right: rightPart,
              };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Find function call in expression
   */
  private static findFunctionCall(expr: string): { name: string; args: string[] } | null {
    const match = expr.match(/^([a-zA-Z_$][a-zA-Z0-9_$\.]*)\s*\((.*)\)$/);
    if (!match) return null;

    const name = match[1];
    const argsStr = match[2];

    if (!argsStr.trim()) {
      return { name, args: [] };
    }

    const args = this.parseArguments(argsStr);
    return { name, args };
  }

  /**
   * Parse function arguments
   */
  private static parseArguments(argsStr: string): string[] {
    const args: string[] = [];
    let current = '';
    let depth = 0;

    for (let i = 0; i < argsStr.length; i++) {
      const char = argsStr[i];

      if ((char === '(' || char === '[' || char === '{') && argsStr[i - 1] !== '\\') {
        depth++;
      } else if ((char === ')' || char === ']' || char === '}') && argsStr[i - 1] !== '\\') {
        depth--;
      } else if (char === ',' && depth === 0) {
        args.push(current.trim());
        current = '';
        continue;
      }

      current += char;
    }

    if (current.trim()) {
      args.push(current.trim());
    }

    return args;
  }

  /**
   * Find array access in expression
   */
  private static findArrayAccess(expr: string): { object: string; index: string } | null {
    let depth = 0;

    for (let i = expr.length - 1; i >= 0; i--) {
      const char = expr[i];

      if (char === ']') {
        depth++;
      } else if (char === '[') {
        depth--;
        if (depth === 0) {
          const objectPart = expr.substring(0, i).trim();
          const indexPart = expr.substring(i + 1, expr.length - 1).trim();

          if (objectPart && indexPart) {
            return {
              object: objectPart,
              index: indexPart,
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Evaluate parsed expression
   */
  private static evaluateParsed(parsed: ParsedExpression, context: EvaluationContext): any {
    switch (parsed.type) {
      case 'literal':
        return parsed.value;

      case 'variable':
        return this.resolvePathInContext(parsed.expression!, context);

      case 'binary':
        return this.evaluateBinary(
          parsed.left!,
          parsed.operator!,
          parsed.right!,
          context
        );

      case 'conditional':
        const testResult = this.evaluateParsed(parsed.test!, context);
        return testResult
          ? this.evaluateParsed(parsed.consequent!, context)
          : this.evaluateParsed(parsed.alternate!, context);

      case 'function':
        return this.evaluateFunction(parsed.callee!, parsed.args!, context);

      case 'array-access':
        const obj = this.evaluateParsed(parsed.object!, context);
        const idx = this.evaluateParsed(parsed.index!, context);
        return Array.isArray(obj) || typeof obj === 'string' ? obj[idx] : null;

      default:
        return null;
    }
  }

  /**
   * Evaluate binary operation
   */
  private static evaluateBinary(
    left: ParsedExpression,
    operator: string,
    right: ParsedExpression,
    context: EvaluationContext
  ): any {
    const leftVal = this.evaluateParsed(left, context);
    const rightVal = this.evaluateParsed(right, context);

    switch (operator) {
      // Arithmetic
      case '+': return leftVal + rightVal;
      case '-': return leftVal - rightVal;
      case '*': return leftVal * rightVal;
      case '/': return rightVal !== 0 ? leftVal / rightVal : null;
      case '%': return leftVal % rightVal;

      // Comparison
      case '===': return leftVal === rightVal;
      case '!==': return leftVal !== rightVal;
      case '==': return leftVal == rightVal;
      case '!=': return leftVal != rightVal;
      case '>': return leftVal > rightVal;
      case '>=': return leftVal >= rightVal;
      case '<': return leftVal < rightVal;
      case '<=': return leftVal <= rightVal;

      // Logical
      case '&&': return leftVal && rightVal;
      case '||': return leftVal || rightVal;

      default:
        return null;
    }
  }

  /**
   * Evaluate function call
   */
  private static evaluateFunction(
    funcName: string,
    args: ParsedExpression[],
    context: EvaluationContext
  ): any {
    const evalArgs = args.map(arg => this.evaluateParsed(arg, context));

    // Check custom functions first
    if (context.functions && context.functions[funcName]) {
      try {
        return context.functions[funcName](...evalArgs);
      } catch (error) {
        console.warn(`Error calling custom function '${funcName}':`, error);
        return null;
      }
    }

    // Check whitelisted functions
    const parts = funcName.split('.');
    if (parts.length === 2) {
      const namespace = parts[0];
      const method = parts[1];
      const whitelist = this.WHITELISTED_FUNCTIONS as any;

      if (whitelist[namespace] && whitelist[namespace][method]) {
        try {
          return whitelist[namespace][method](...evalArgs);
        } catch (error) {
          console.warn(`Error calling function '${funcName}':`, error);
          return null;
        }
      }
    }

    console.warn(`Function '${funcName}' not found in whitelist`);
    return null;
  }
}
